import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { useAuthContext } from '../../hooks/UseAuthContext';
import { toast } from 'react-toastify';
import api from '../../api';
import Loader from '../../Components/Loader/Loader';

// ═══ 🎵 MUSIC LIBRARY ═══════════════════════════════════════
const MUSIC_LIBRARY = [
  { title: 'Sly Sky - Telecasted', mood: 'melancholic' },
  { title: 'No.2 Remembering Her - Esther Abrami', mood: 'melancholic' },
  { title: 'Champion - Telecasted', mood: 'chill' },
  { title: 'Oh Please - Telecasted', mood: 'chill' },
  { title: 'Jetski - Telecasted', mood: 'uneasy' },
  { title: 'Phantom - Density & Time', mood: 'uneasy' },
  { title: 'On The Hunt - Andrew Langdon', mood: 'uneasy' },
  { title: 'Name The Time And Place - Telecasted', mood: 'excited' },
  { title: 'Delayed Baggage - Ryan Stasik', mood: 'euphoric' },
  { title: 'Like It Loud - Dyalla', mood: 'euphoric' },
  { title: 'Organic Guitar House - Dyalla', mood: 'euphoric' },
  { title: 'Honey, I Dismembered The Kids - Ezra Lipp', mood: 'dark' },
  { title: 'Night Hunt - Jimena Contreras', mood: 'dark' },
  { title: 'Curse of the Witches - Jimena Contreras', mood: 'dark' },
  { title: 'Restless Heart - Jimena Contreras', mood: 'sad' },
  { title: 'Heartbeat Of The Wind - Asher Fulero', mood: 'sad' },
  { title: 'Hopeless - Jimena Contreras', mood: 'sad' },
  { title: 'Touch - Anno Domini Beats', mood: 'happy' },
  { title: 'Cafecito por la Manana - Cumbia Deli', mood: 'happy' },
  { title: 'Aurora on the Boulevard - National Sweetheart', mood: 'happy' },
  { title: 'Buckle Up - Jeremy Korpas', mood: 'angry' },
  { title: 'Twin Engines - Jeremy Korpas', mood: 'angry' },
  { title: 'Hopeful - Nat Keefe', mood: 'hopeful' },
  { title: 'Hopeful Freedom - Asher Fulero', mood: 'hopeful' },
  { title: 'Crystaline - Quincas Moreira', mood: 'contemplative' },
  { title: 'Final Soliloquy - Asher Fulero', mood: 'contemplative' },
  { title: 'Seagull - Telecasted', mood: 'funny' },
  { title: 'Banjo Doops - Joel Cummins', mood: 'funny' },
  { title: 'Baby Animals Playing - Joel Cummins', mood: 'funny' },
  { title: 'Sinister - Anno Domini Beats', mood: 'dark' },
  { title: 'Traversing - Godmode', mood: 'dark' },
];

const MOOD_OPTIONS = [
  { value: 'happy', label: '😊 Happy' },
  { value: 'sad', label: '😢 Sad' },
  { value: 'hopeful', label: '🌅 Hopeful' },
  { value: 'melancholic', label: '💭 Melancholic' },
  { value: 'dark', label: '🌑 Dark' },
  { value: 'chill', label: '🪶 Chill' },
  { value: 'funny', label: '😂 Funny' },
  { value: 'uneasy', label: '😬 Uneasy' },
  { value: 'euphoric', label: '⚡ Euphoric' },
  { value: 'angry', label: '🔥 Angry' },
  { value: 'excited', label: '🚀 Excited' },
  { value: 'contemplative', label: '🧘 Contemplative' },
];

function BgMusic({ onSelectMusic, selectedMusic = '' }) {
  const { user } = useAuthContext();

  const [activeMusic, setActiveMusic] = useState(selectedMusic);
  const [selectedMood, setSelectedMood] = useState('happy');
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const playingWaveRef = useRef(null);

  // ── Sync from parent ──────────────────────────────────────
  useEffect(() => {
    if (selectedMusic) setActiveMusic(selectedMusic);
  }, [selectedMusic]);

  // ── Filter by mood ────────────────────────────────────────
  useEffect(() => {
    const filtered = MUSIC_LIBRARY.filter((m) => m.mood === selectedMood);
    setFilteredList(filtered);

    // Stop playback when mood changes
    if (playingWaveRef.current) {
      playingWaveRef.current.ws.destroy();
      playingWaveRef.current = null;
    }
    setPlayingIndex(null);
  }, [selectedMood]);

  // ── Create waveform ───────────────────────────────────────
  const createWaveform = useCallback((containerId, url) => {
    const container = document.getElementById(containerId);
    if (!container) return null;

    container.innerHTML = '';
    const ws = WaveSurfer.create({
      container,
      waveColor: 'rgba(136, 136, 168, 0.4)',
      progressColor: '#7c3aed',
      cursorColor: '#a78bfa',
      barWidth: 2,
      barRadius: 2,
      height: 60,
      responsive: true,
      normalize: true,
      dragToSeek: true,
    });

    ws.load(url);
    return ws;
  }, []);

  // ── Play / Pause toggle ───────────────────────────────────
  const handlePlayPause = useCallback((id, url) => {
    const current = playingWaveRef.current;

    // Stop and destroy any other playing waveform
    if (current && current.id !== id) {
      current.ws.pause();
      current.ws.destroy();
      playingWaveRef.current = null;
      setPlayingIndex(null);
    }

    // Start new or toggle existing
    if (!current || current.id !== id) {
      const ws = createWaveform(`waveform-${id}`, url);
      if (!ws) return;

      playingWaveRef.current = { id, ws };
      setPlayingIndex(id);

      ws.on('ready', () => ws.play());
      ws.on('finish', () => {
        ws.destroy();
        playingWaveRef.current = null;
        setPlayingIndex(null);
      });
    } else {
      if (current.ws.isPlaying()) {
        current.ws.pause();
        setPlayingIndex(null);
      } else {
        current.ws.play();
        setPlayingIndex(id);
      }
    }
  }, [createWaveform]);

  // ── Handle selecting music ────────────────────────────────
  const handleSelectMusic = useCallback((musicTitle) => {
    const name = typeof musicTitle === 'string' ? musicTitle.trim() : musicTitle;
    setActiveMusic(name);
    onSelectMusic?.(name);
  }, [onSelectMusic]);

  return (
    <div className="bgmusic-container">
      <div className="bgmusic">
        <h2>Background Music</h2>
        <p>Preview and select the perfect background music that fits your emotion.</p>

        {/* ── Mood Filter ─────────────────────────────── */}
        <div className="bgmusic-filter">
          <label>Filter by emotion:</label>
          <select
            value={selectedMood}
            onChange={(e) => setSelectedMood(e.target.value)}
          >
            {MOOD_OPTIONS.map((mood) => (
              <option key={mood.value} value={mood.value}>
                {mood.label}
              </option>
            ))}
          </select>
        </div>

        {/* ── Music List ──────────────────────────────── */}
        <div className="bgmusic-list">
          {filteredList.map((music, index) => {
            const audioUrl = `${process.env.REACT_APP_API_URL_VIDS}/bg-music/${music.title}.mp3`;
            const isActive = activeMusic === music.title;
            const isPlaying = playingIndex === index;

            return (
              <div key={index} className="bgmusic-item">
                <div className="bgmusic-item-header">
                  <h3>
                    {index + 1}. {music.title}
                  </h3>
                  <div className="bgmusic-checkbox">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => handleSelectMusic(music.title)}
                      aria-label={`Select ${music.title}`}
                    />
                    <svg viewBox="0 0 35.6 35.6">
                      <circle className="background" cx="17.8" cy="17.8" r="17.8" />
                      <circle className="stroke" cx="17.8" cy="17.8" r="14.37" />
                      <polyline className="check" points="11.78 18.12 15.55 22.23 25.17 12.87" />
                    </svg>
                  </div>
                </div>

                {/* ── Waveform ────────────────────────── */}
                <div className="bgmusic-player">
                  <div
                    id={`waveform-${index}`}
                    className="bgmusic-waveform"
                    aria-label="Audio waveform"
                  />
                  <button
                    onClick={() => handlePlayPause(index, audioUrl)}
                    className="bgmusic-voice-btn"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? '⏸ Pause' : '▶ Play'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && <Loader className="bgmusic-loader" />}
    </div>
  );
}

export default BgMusic;